/**
 * Transformation diagrams — 2D architecture map (default), optional 3D explore.
 * Landscape: hub-and-spoke like the connected-system poster (central ERP,
 * satellites by layer, orthogonal labeled edges). Dataflow keeps columns.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ForceGraph3D from '3d-force-graph';
import SpriteText from 'three-spritetext';
import * as THREE from 'three';
import AuraExternalSystemsLegend from '@/components/AuraExternalSystemsLegend';
import { diagramNodeSource } from '@/lib/auraSourceAttribution';
import { lookupExternalSystemForNode } from '@/lib/auraExternalSystems';

const TYPE_COLORS = {
  sap: { fill: '#3b4f7a', stroke: '#8bb4ff', text: '#f0f6ff' },
  s4: { fill: '#2d5a3d', stroke: '#6ee7a0', text: '#ecfdf5' },
  db: { fill: '#4a3a6b', stroke: '#c4b5fd', text: '#f5f3ff' },
  os: { fill: '#3f3f46', stroke: '#a1a1aa', text: '#f4f4f5' },
  integration: { fill: '#5b3a1e', stroke: '#fdba74', text: '#fff7ed' },
  middleware: { fill: '#5b3a1e', stroke: '#fdba74', text: '#fff7ed' },
  external: { fill: '#1e3a4a', stroke: '#7dd3fc', text: '#f0f9ff' },
  system: { fill: '#3b4f7a', stroke: '#93c5fd', text: '#eff6ff' },
  process: { fill: '#334155', stroke: '#94a3b8', text: '#f8fafc' },
  interface: { fill: '#5b3a1e', stroke: '#fbbf24', text: '#fffbeb' },
  hub: { fill: '#4c1d95', stroke: '#c4b5fd', text: '#f5f3ff' },
  data: { fill: '#164e63', stroke: '#67e8f9', text: '#ecfeff' },
  other: { fill: '#27272a', stroke: '#71717a', text: '#fafafa' },
  desktop: { fill: '#1e3a4a', stroke: '#7dd3fc', text: '#f0f9ff' },
  unused: { fill: '#1f2937', stroke: '#6b7280', text: '#d1d5db' },
};

const LANDSCAPE_LANES = [
  { id: 'infra', label: 'Infrastructure', hint: 'OS · kernel · DB' },
  { id: 'platform', label: 'Platform', hint: 'Runtime / technology' },
  { id: 'application', label: 'Application', hint: 'ERP · S/4 · systems' },
  { id: 'integration', label: 'Interfaces', hint: 'IDoc · RFC · OData…' },
  { id: 'external', label: 'External & data', hint: 'Partners · BW · files' },
];

/** Connected-component landscape (named partners, not generic hubs). */
const CONNECTED_LANES = [
  { id: 'desktop', label: 'User / desktop', hint: 'GUI · RFC clients' },
  { id: 'saas', label: 'SaaS / cloud', hint: 'Ariba · networks' },
  { id: 'onprem', label: 'On-premise apps', hint: 'Named on-prem only' },
  { id: 'application', label: 'Core ERP', hint: 'Analyzed SID · S/4' },
  { id: 'unused', label: 'Configured, unused', hint: 'Dashed connections' },
];

const FLOW_LANES = [
  { id: 'source', label: 'Sources', hint: 'Partners / uploads (only if named)' },
  { id: 'interface', label: 'Interface channels', hint: 'IDoc · RFC · OData · SLT' },
  { id: 'core', label: 'Core system', hint: 'Analyzed SID' },
  { id: 'target', label: 'Destinations', hint: 'S/4 · CVI · consumers' },
  { id: 'analytics', label: 'Analytics & replication', hint: 'BW · SLT targets' },
];

/** Poster-style column colors (same palette in 2D and 3D). */
const LANE_THEME = {
  infra: { fill: '#122033', stroke: '#3b82c4', title: '#93c5fd' },
  platform: { fill: '#1a1533', stroke: '#8b5cf6', title: '#c4b5fd' },
  application: { fill: '#0f2744', stroke: '#3b82f6', title: '#bfdbfe' },
  integration: { fill: '#231810', stroke: '#f59e0b', title: '#fcd34d' },
  external: { fill: '#10261f', stroke: '#34d399', title: '#6ee7b7' },
  source: { fill: '#122033', stroke: '#3b82c4', title: '#93c5fd' },
  interface: { fill: '#24143a', stroke: '#a78bfa', title: '#ddd6fe' },
  core: { fill: '#0f2744', stroke: '#60a5fa', title: '#bfdbfe' },
  target: { fill: '#10261f', stroke: '#34d399', title: '#6ee7b7' },
  analytics: { fill: '#2a1a0e', stroke: '#fb923c', title: '#fdba74' },
  desktop: { fill: '#122033', stroke: '#38bdf8', title: '#7dd3fc' },
  saas: { fill: '#10261f', stroke: '#34d399', title: '#6ee7b7' },
  onprem: { fill: '#1a1533', stroke: '#a78bfa', title: '#ddd6fe' },
  unused: { fill: '#111827', stroke: '#6b7280', title: '#9ca3af' },
};

function laneTheme(id) {
  return LANE_THEME[id] || { fill: '#111827', stroke: '#334155', title: '#94a3b8' };
}

const FILTER_PRESETS = [
  { id: 'all', label: 'All' },
  { id: 'stack', label: 'Stack only', layers: ['infra', 'platform', 'application'], types: null },
  { id: 'interfaces', label: 'Interfaces', layers: ['integration', 'interface', 'external', 'source', 'target', 'analytics', 'desktop', 'saas', 'onprem', 'unused'], types: null },
  { id: 'systems', label: 'Systems', types: ['sap', 's4', 'system', 'db', 'external', 'desktop'] },
];

function colorFor(type) {
  return TYPE_COLORS[(type || 'other').toLowerCase()] || TYPE_COLORS.other;
}

function resolveLayer(n, lanes) {
  let layer = (n.layer || '').toLowerCase();
  const laneIds = lanes.map((l) => l.id);
  if (layer && laneIds.includes(layer)) return layer;

  const t = (n.type || '').toLowerCase();
  if (laneIds.includes('desktop') || laneIds.includes('saas') || laneIds.includes('onprem')) {
    if (layer && laneIds.includes(layer)) return layer;
    if (['sap', 's4', 'system'].includes(t)) return laneIds.includes('application') ? 'application' : 'onprem';
    if (['db', 'os'].includes(t)) return laneIds.includes('onprem') ? 'onprem' : 'application';
    if (t === 'unused') return 'unused';
    if (['external', 'desktop'].includes(t)) return laneIds.includes('saas') ? 'saas' : 'desktop';
    return laneIds.includes('onprem') ? 'onprem' : laneIds[0];
  }
  // Map types into appropriate lane set
  if (laneIds.includes('core')) {
    // flow diagram mapping
    if (['sap', 's4', 'system'].includes(t)) return 'core';
    if (['hub', 'interface', 'integration', 'middleware'].includes(t)) return 'interface';
    if (['data', 'external'].includes(t) && /bw|analytics/i.test(`${n.label} ${n.id}`)) return 'analytics';
    if (['data', 'external', 'process'].includes(t)) return 'source';
    return laneIds.includes(layer) ? layer : 'interface';
  }
  // landscape
  if (['db', 'os'].includes(t)) return 'infra';
  if (['sap', 's4', 'system'].includes(t)) return 'application';
  if (['integration', 'interface', 'hub', 'middleware'].includes(t)) return 'integration';
  if (['external', 'data'].includes(t)) return 'external';
  if (layer && laneIds.includes(layer)) return layer;
  return 'application';
}

function pickLanes(layoutHint, nodes) {
  const hint = (layoutHint || 'flow').toLowerCase();
  const wantsConnected = nodes.some((n) =>
    CONNECTED_LANES.some((l) => l.id === (n.layer || '').toLowerCase()),
  );
  const wantsFlow =
    !wantsConnected
    && (hint === 'flow'
      || nodes.some((n) => FLOW_LANES.some((l) => l.id === (n.layer || '').toLowerCase())));
  const all = wantsConnected ? CONNECTED_LANES : wantsFlow ? FLOW_LANES : LANDSCAPE_LANES;
  const used = new Set(nodes.map((n) => resolveLayer(n, all)));
  const filtered = all.filter((l) => used.has(l.id));
  return filtered.length ? filtered : all;
}

function isFlowDiagram(layoutHint, nodes) {
  const hint = (layoutHint || '').toLowerCase();
  if (hint === 'layered' || hint === 'radial') return false;
  return (
    hint === 'flow'
    || nodes.some((n) => FLOW_LANES.some((l) => l.id === (n.layer || '').toLowerCase()))
  );
}

function pickHero(nodes, edges) {
  const deg = {};
  edges.forEach((e) => {
    deg[e.from] = (deg[e.from] || 0) + 1;
    deg[e.to] = (deg[e.to] || 0) + 1;
  });
  let best = nodes[0];
  let bestScore = -1;
  nodes.forEach((n) => {
    const t = (n.type || '').toLowerCase();
    const blob = `${n.label || ''} ${n.id || ''} ${n.subtitle || ''}`;
    let score = deg[n.id] || 0;
    if (['sap', 's4', 'system'].includes(t)) score += 40;
    if ((n.layer || '').toLowerCase() === 'application') score += 18;
    if ((n.layer || '').toLowerCase() === 'core') score += 50;
    if (/erp|ecc|\bprd\b|client/i.test(blob)) score += 24;
    if (/s\/4|s4hana/i.test(blob) && !/erp|ecc/i.test(blob)) score -= 8;
    if (score > bestScore) {
      best = n;
      bestScore = score;
    }
  });
  return best;
}

function regionFor(layer, n) {
  const l = (layer || '').toLowerCase();
  if (l === 'source') return 'west';
  if (l === 'interface') return 'north';
  if (l === 'target') return 'east';
  if (l === 'analytics') return 'south';
  if (l === 'core') return 'east';
  if (l === 'unused') {
    const t = (n?.type || '').toLowerCase();
    if (['external', 'saas'].includes(t)) return 'west';
    if (['sap', 's4', 'system', 'db'].includes(t)) return 'north';
    return 'south';
  }
  if (l === 'saas' || l === 'external') return 'west';
  if (l === 'desktop') return 'south';
  if (l === 'onprem' || l === 'infra' || l === 'platform') return 'north';
  if (l === 'application' || l === 'integration') return 'east';
  return 'north';
}

function cardStyle(n, isHero) {
  const layer = (n._layer || n.layer || '').toLowerCase();
  const unused = layer === 'unused' || (n.type || '').toLowerCase() === 'unused';
  if (isHero) {
    return {
      fill: '#f8fafc',
      stroke: '#0f172a',
      text: '#0f172a',
      sub: '#334155',
      hatch: false,
      dashed: false,
    };
  }
  if (unused) {
    return {
      fill: '#0b1220',
      stroke: '#94a3b8',
      text: '#e2e8f0',
      sub: '#94a3b8',
      hatch: false,
      dashed: true,
    };
  }
  if (layer === 'desktop') {
    return {
      fill: '#1e293b',
      stroke: '#cbd5e1',
      text: '#f8fafc',
      sub: '#94a3b8',
      hatch: true,
      dashed: false,
    };
  }
  if (layer === 'saas' || layer === 'external' || layer === 'source') {
    return {
      fill: '#475569',
      stroke: '#e2e8f0',
      text: '#f8fafc',
      sub: '#cbd5e1',
      hatch: false,
      dashed: false,
    };
  }
  if (layer === 'interface') {
    return {
      fill: '#3b3358',
      stroke: '#c4b5fd',
      text: '#f5f3ff',
      sub: '#ddd6fe',
      hatch: false,
      dashed: false,
    };
  }
  if (layer === 'target') {
    return {
      fill: '#ecfdf5',
      stroke: '#065f46',
      text: '#064e3b',
      sub: '#047857',
      hatch: false,
      dashed: false,
    };
  }
  if (layer === 'analytics') {
    return {
      fill: '#2a1a0e',
      stroke: '#fdba74',
      text: '#fff7ed',
      sub: '#fdba74',
      hatch: false,
      dashed: false,
    };
  }
  return {
    fill: '#f1f5f9',
    stroke: '#0f172a',
    text: '#0f172a',
    sub: '#475569',
    hatch: false,
    dashed: false,
  };
}

const AREA_CODE_RULES = [
  [/asset account|fi-aa|\baa\b/i, 'AA'],
  [/general ledger|fi-gl|\bgl\b/i, 'FI'],
  [/accounts receivable|fi-ar|\bar\b/i, 'AR'],
  [/accounts payable|fi-ap|\bap\b/i, 'AP'],
  [/controlling|\bco\b/i, 'CO'],
  [/material ledger|\bml\b/i, 'ML'],
  [/materials management|\bmm\b/i, 'MM'],
  [/sales and dist|\bsd\b/i, 'SD'],
  [/plant maintenance|\bpm\b/i, 'PM'],
  [/quality management|\bqm\b/i, 'QM'],
  [/project system|\bps\b/i, 'PS'],
  [/warehouse|\bewm\b|\bwm\b/i, 'WM'],
  [/production planning|\bpp\b/i, 'PP'],
  [/human capital|\bhcm\b|\bhr\b/i, 'HR'],
  [/business partner|\bcvi\b/i, 'CVI'],
];

function moduleCodeFromArea(area) {
  const code = String(area?.code || '').trim();
  if (code && code.length <= 8) return code.replace(/^FI-/, '').slice(0, 8);
  const blob = `${area?.code || ''} ${area?.name || ''}`;
  for (const [re, mapped] of AREA_CODE_RULES) {
    if (re.test(blob)) return mapped;
  }
  const name = String(area?.name || code || '').trim();
  return name.slice(0, 8) || 'MOD';
}

function areasToModules(areas) {
  if (!Array.isArray(areas)) return [];
  const seen = new Set();
  const out = [];
  areas.forEach((area) => {
    if (!area?.name && !area?.code) return;
    const code = moduleCodeFromArea(area);
    const key = code.toUpperCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      code,
      name: String(area.name || code).slice(0, 48),
      note: String(area.note || area.evidence || '').slice(0, 80),
    });
  });
  if (seen.has('FI') && seen.has('CO')) {
    const fi = out.find((m) => m.code.toUpperCase() === 'FI');
    const rest = out.filter((m) => !['FI', 'CO'].includes(m.code.toUpperCase()));
    return [{ code: 'FI/CO', name: 'Financials / Controlling', note: fi?.note || '' }, ...rest].slice(0, 12);
  }
  return out.slice(0, 12);
}

function nodeModules(n, fallback) {
  if (Array.isArray(n?.modules) && n.modules.length) {
    return n.modules
      .map((m) => ({
        code: String(m.code || m.id || m.name || '').slice(0, 12),
        name: String(m.name || m.label || m.code || '').slice(0, 48),
        note: String(m.note || '').slice(0, 80),
      }))
      .filter((m) => m.code);
  }
  return fallback || [];
}

function placeRow(items, cx, y, gap, positions) {
  if (!items.length) return;
  const total = items.reduce((s, n) => s + n._w, 0) + gap * (items.length - 1);
  let x = cx - total / 2;
  items.forEach((n) => {
    positions[n.id] = {
      x: x + n._w / 2,
      y,
      w: n._w,
      h: n._h,
      node: n,
      layer: n._layer,
      hero: !!n._hero,
    };
    x += n._w + gap;
  });
}

function placeCol(items, x, cy, gap, positions) {
  if (!items.length) return;
  const total = items.reduce((s, n) => s + n._h, 0) + gap * (items.length - 1);
  let y = cy - total / 2;
  items.forEach((n) => {
    positions[n.id] = {
      x,
      y: y + n._h / 2,
      w: n._w,
      h: n._h,
      node: n,
      layer: n._layer,
      hero: !!n._hero,
    };
    y += n._h + gap;
  });
}

/** Hub-and-spoke architecture map (connected landscape poster or dataflow template). */
function layoutHub2d(nodes, edges, fallbackModules, mode = 'landscape') {
  const isDataflow = mode === 'dataflow';
  const lanes = pickLanes(isDataflow ? 'flow' : 'layered', nodes);
  const heroSrc = pickHero(nodes, edges) || nodes[0];
  const heroMods = nodeModules(heroSrc, fallbackModules);
  const deg = {};
  edges.forEach((e) => {
    deg[e.from] = (deg[e.from] || 0) + 1;
    deg[e.to] = (deg[e.to] || 0) + 1;
  });

  const enriched = nodes.map((n) => {
    const layer = resolveLayer(n, lanes);
    const isHero = n.id === heroSrc.id;
    const extra = (n.subtitle ? 1 : 0) + Math.min(3, (n.metrics || []).length);
    const mods = isHero ? heroMods : [];
    const tileRows = mods.length ? Math.ceil(Math.min(mods.length, 8) / 4) : 0;
    const featured = mods.find((m) => /pm|work order|plant maint/i.test(`${m.code} ${m.name} ${m.note}`));
    const stripH = isDataflow && !isHero ? 16 : 0;
    return {
      ...n,
      _layer: layer,
      _hero: isHero,
      _modules: mods,
      _featured: featured || null,
      _functionStrip: isDataflow && !isHero ? functionStripLabel(n) : '',
      _w: isHero
        ? Math.max(isDataflow ? 360 : 320, 72 + String(n.label || '').length * 7, Math.min(4, mods.length || 1) * 78 + 28)
        : (isDataflow ? Math.min(156, Math.max(112, 52 + String(n.label || '').length * 5.5)) : nodeW(n)),
      _h: isHero
        ? Math.max(isDataflow ? 108 : 128, 62 + extra * 13 + tileRows * 30 + (featured ? 26 : 0) + 8)
        : (isDataflow ? 44 + stripH + (n.subtitle ? 10 : 0) : nodeH(n)),
    };
  });

  const groups = { north: [], west: [], east: [], south: [] };
  enriched.forEach((n) => {
    if (n._hero) return;
    groups[regionFor(n._layer, n)].push(n);
  });
  Object.keys(groups).forEach((k) => {
    groups[k].sort((a, b) => (deg[b.id] || 0) - (deg[a.id] || 0));
  });

  const gapX = isDataflow ? 22 : 18;
  const gapY = isDataflow ? 20 : 16;
  const northH = groups.north.reduce((m, n) => Math.max(m, n._h), 0);
  const southRows = [];
  const perRow = groups.south.length > 6 ? Math.ceil(groups.south.length / 2) : groups.south.length || 1;
  for (let i = 0; i < groups.south.length; i += perRow) {
    southRows.push(groups.south.slice(i, i + perRow));
  }
  const southH = groups.south.reduce((m, n) => Math.max(m, n._h), 0);
  const southBlock = southRows.length
    ? southRows.length * southH + (southRows.length - 1) * 14
    : 0;
  const westW = groups.west.reduce((m, n) => Math.max(m, n._w), 0);
  const eastW = groups.east.reduce((m, n) => Math.max(m, n._w), 0);
  const hero = enriched.find((n) => n._hero);
  const rowW = (list) =>
    list.reduce((s, n) => s + n._w, 0) + gapX * Math.max(0, list.length - 1);
  const innerW = Math.max(
    hero._w + 48,
    rowW(groups.north),
    ...southRows.map((r) => rowW(r)),
    420,
  );
  const leftGutter = 78;
  const width = Math.max(
    720,
    leftGutter + (westW ? westW + 28 : 12) + innerW + (eastW ? eastW + 28 : 12) + 28,
  );
  const height = Math.max(
    420,
    32 + (northH ? northH + 40 : 16) + hero._h + (southBlock ? southBlock + 48 : 24) + 36,
  );

  const cx = leftGutter + (westW ? westW + 28 : 12) + innerW / 2;
  const northY = 28 + (northH ? northH / 2 : 0);
  const cy = 32 + (northH ? northH + (isDataflow ? 48 : 40) : 16) + hero._h / 2;
  const southTop = cy + hero._h / 2 + (isDataflow ? 48 : 40);

  const positions = {};
  positions[hero.id] = {
    x: cx,
    y: cy,
    w: hero._w,
    h: hero._h,
    node: hero,
    layer: hero._layer,
    hero: true,
  };
  placeRow(groups.north, cx, northY || 40, gapX, positions);
  placeCol(
    groups.west,
    leftGutter + (westW ? westW / 2 : 0),
    cy,
    gapY,
    positions,
  );
  placeCol(
    groups.east,
    width - 28 - (eastW ? eastW / 2 : 0),
    cy,
    gapY,
    positions,
  );
  southRows.forEach((row, i) => {
    placeRow(row, cx, southTop + i * (southH + 14) + southH / 2, gapX, positions);
  });

  return {
    kind: isDataflow ? 'dataflow' : 'hub',
    positions,
    width,
    height: isDataflow ? height + 36 : height,
    lanes,
    colW: 0,
    laneHeader: 0,
    topPad: 8,
    heroId: hero.id,
    flow: isDataflow || isFlowDiagram('flow', nodes),
    sideLabel: isDataflow ? 'Sources' : (isFlowDiagram('flow', nodes) && groups.west.length ? 'Sources' : ''),
    bands: (isDataflow || isFlowDiagram('flow', nodes))
      ? [
        { id: 'interface', label: 'Interface channels', y: 8, h: Math.max(cy - hero._h / 2 - 8, 80) },
        { id: 'core', label: 'Core system', y: cy - hero._h / 2 - 8, h: hero._h + 16 },
        { id: 'analytics', label: 'Destinations / analytics', y: cy + hero._h / 2 + 8, h: height - (cy + hero._h / 2) - 16 },
      ]
      : [
        { id: 'saas', label: 'SaaS / cloud', y: 8, h: Math.max(cy - hero._h / 2 - 8, 80) },
        { id: 'onprem', label: 'On-premise', y: cy - hero._h / 2 - 8, h: hero._h + 16 },
        { id: 'desktop', label: 'User / desktop', y: cy + hero._h / 2 + 8, h: height - (cy + hero._h / 2) - 16 },
      ],
  };
}

function sidePoint(p, side, t) {
  const x0 = p.x - p.w / 2;
  const y0 = p.y - p.h / 2;
  const tt = Math.min(0.86, Math.max(0.14, t));
  if (side === 'top') return { x: x0 + p.w * tt, y: y0 };
  if (side === 'bottom') return { x: x0 + p.w * tt, y: y0 + p.h };
  if (side === 'left') return { x: x0, y: y0 + p.h * tt };
  return { x: x0 + p.w, y: y0 + p.h * tt };
}

function pickSides(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) > Math.abs(dy) * 0.85) {
    return dx > 0 ? ['right', 'left'] : ['left', 'right'];
  }
  return dy > 0 ? ['bottom', 'top'] : ['top', 'bottom'];
}

function edgeOrtho(a, b, slot = 0.5) {
  const [sa, sb] = pickSides(a, b);
  const p1 = sidePoint(a, sa, slot);
  const p2 = sidePoint(b, sb, 1 - slot);
  if (sa === 'left' || sa === 'right') {
    const midX = (p1.x + p2.x) / 2;
    return {
      d: `M ${p1.x} ${p1.y} L ${midX} ${p1.y} L ${midX} ${p2.y} L ${p2.x} ${p2.y}`,
      lx: midX,
      ly: (p1.y + p2.y) / 2,
    };
  }
  const midY = (p1.y + p2.y) / 2;
  return {
    d: `M ${p1.x} ${p1.y} L ${p1.x} ${midY} L ${p2.x} ${midY} L ${p2.x} ${p2.y}`,
    lx: (p1.x + p2.x) / 2,
    ly: midY,
  };
}

function nodeW(n) {
  const label = n.label || n.id || '';
  return Math.min(196, Math.max(132, 48 + label.length * 6.2));
}

function nodeH(n) {
  const extra = (n.subtitle ? 1 : 0) + Math.min(2, (n.metrics || []).length);
  return 38 + extra * 13;
}

/** Layered left→right swimlane layout */
function layout2d(nodes, edges, lanes) {
  const enriched = nodes.map((n) => {
    const layer = resolveLayer(n, lanes);
    return { ...n, _layer: layer, _w: nodeW(n), _h: nodeH(n) };
  });

  const colGap = 48;
  const rowGap = 16;
  const lanePadX = 16;
  const laneHeader = 44;
  const topPad = 12;
  const bottomPad = 20;
  const colW = Math.max(200, ...enriched.map((n) => n._w)) + lanePadX * 2;

  const buckets = {};
  lanes.forEach((l) => {
    buckets[l.id] = [];
  });
  enriched.forEach((n) => {
    const key = buckets[n._layer] ? n._layer : lanes[Math.floor(lanes.length / 2)].id;
    buckets[key].push(n);
  });

  // Put highest-degree / SAP nodes higher in their column
  const deg = {};
  edges.forEach((e) => {
    deg[e.from] = (deg[e.from] || 0) + 1;
    deg[e.to] = (deg[e.to] || 0) + 1;
  });
  Object.keys(buckets).forEach((k) => {
    buckets[k].sort((a, b) => {
      const score = (n) => {
        const t = (n.type || '').toLowerCase();
        const hero = ['sap', 's4', 'system'].includes(t) ? 100 : 0;
        return hero + (deg[n.id] || 0);
      };
      return score(b) - score(a);
    });
  });

  const positions = {};
  let maxColH = 0;
  lanes.forEach((lane, col) => {
    const list = buckets[lane.id] || [];
    let y = topPad + laneHeader;
    list.forEach((n) => {
      positions[n.id] = {
        x: col * colW + colW / 2,
        y: y + n._h / 2,
        w: n._w,
        h: n._h,
        node: n,
        layer: lane.id,
      };
      y += n._h + rowGap;
    });
    maxColH = Math.max(maxColH, y);
  });

  const width = Math.max(640, lanes.length * colW + 8);
  const height = Math.max(280, maxColH + bottomPad);

  return {
    kind: 'flow',
    positions,
    width,
    height,
    colW,
    lanes,
    laneHeader,
    topPad,
  };
}

function edgeCurve(a, b) {
  const x1 = a.x;
  const y1 = a.y;
  const x2 = b.x;
  const y2 = b.y;
  const mx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}

function parseVolume(text) {
  if (!text) return null;
  const m = String(text).match(/(\d[\d,]*)/);
  return m ? Number(m[1].replace(/,/g, '')) : null;
}

function edgeStrokeWidth(edge) {
  const v = parseVolume(edge.label);
  if (v == null) return 1.5;
  if (v >= 100) return 3.2;
  if (v >= 20) return 2.4;
  return 1.8;
}

function edgeHasRisk(edge) {
  return /unavailable|blocked|fail|error/i.test(edge.label || '');
}

function edgeIsDashed(edge) {
  return (
    (edge.style || '').toLowerCase() === 'dashed'
    || (edge.kind || '').toLowerCase() === 'unused'
    || /not used|unused|configured.?only/i.test(edge.label || '')
  );
}

/** Data-type arrow colors (SAP S/4 dataflow poster template). */
const DATA_TYPE_COLORS = [
  { re: /customer|\bbp\b|cvi|debtor|creditor/i, color: '#2563eb', label: 'Customer' },
  { re: /\bevent\b|notification|alert/i, color: '#38bdf8', label: 'Event' },
  { re: /\border\b|sales|so\b|purchase/i, color: '#0d9488', label: 'Order' },
  { re: /\bpart\b|material|\bmm\b|product/i, color: '#16a34a', label: 'Part' },
  { re: /invoice|billing|\bar\b|revenue/i, color: '#ea580c', label: 'Invoice' },
  { re: /payment|cash|treasury|bank/i, color: '#fb923c', label: 'Payment' },
  { re: /\btax\b|vat|withhold/i, color: '#9333ea', label: 'Tax' },
  { re: /labor|\bhr\b|hcm|time|payroll/i, color: '#dc2626', label: 'Labor' },
  { re: /people|user|identity|auth/i, color: '#525252', label: 'People' },
];

const FUNCTION_STRIP_COLORS = {
  growth: { fill: '#1e3a5f', text: '#f8fafc' },
  financials: { fill: '#ca8a04', text: '#1c1917' },
  deliver: { fill: '#15803d', text: '#f0fdf4' },
  labor: { fill: '#be185d', text: '#fdf2f8' },
  reporting: { fill: '#334155', text: '#f1f5f9' },
  integration: { fill: '#5b21b6', text: '#f5f3ff' },
  default: { fill: '#334155', text: '#f1f5f9' },
};

function edgeDataTypeColor(edge) {
  const blob = `${edge.data_type || ''} ${edge.label || ''} ${edge.kind || ''}`;
  for (const rule of DATA_TYPE_COLORS) {
    if (rule.re.test(blob)) return rule.color;
  }
  return null;
}

function functionStripStyle(n) {
  const blob = `${(n.metrics || []).join(' ')} ${n.subtitle || ''} ${n.type || ''} ${n.layer || ''}`.toLowerCase();
  if (/growth|crm|sales force|market/i.test(blob)) return FUNCTION_STRIP_COLORS.growth;
  if (/financ|ledger|gl|invoice|payment|treasury|fi\b|co\b|ar\b|ap\b/i.test(blob)) {
    return FUNCTION_STRIP_COLORS.financials;
  }
  if (/deliver|logistic|warehouse|mm\b|sd\b|supply|ship/i.test(blob)) return FUNCTION_STRIP_COLORS.deliver;
  if (/labor|hr\b|hcm|payroll|time/i.test(blob)) return FUNCTION_STRIP_COLORS.labor;
  if (/report|analytics|bw\b|bi\b|dashboard/i.test(blob)) return FUNCTION_STRIP_COLORS.reporting;
  if (/idoc|rfc|odata|interface|middleware|gateway/i.test(blob)) return FUNCTION_STRIP_COLORS.integration;
  return FUNCTION_STRIP_COLORS.default;
}

function functionStripLabel(n) {
  const m = (n.metrics || []).find(Boolean);
  if (m) return String(m).slice(0, 28);
  if (n.subtitle) return String(n.subtitle).slice(0, 28);
  const layer = (n._layer || n.layer || '').toLowerCase();
  if (layer === 'source') return 'Source';
  if (layer === 'interface') return 'Interface';
  if (layer === 'target') return 'Destination';
  if (layer === 'analytics') return 'Analytics';
  return 'Data exchange';
}

function edgePathBetween(a, b, layoutKind, idx) {
  const slot = 0.22 + (idx % 7) * 0.08;
  const [sa, sb] = pickSides(a, b);
  const p1 = sidePoint(a, sa, slot);
  const p2 = sidePoint(b, sb, 1 - slot);
  if (layoutKind === 'dataflow') {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const c1x = p1.x + dx * 0.5;
    const c1y = p1.y + dy * 0.08;
    const c2x = p2.x - dx * 0.5;
    const c2y = p2.y - dy * 0.08;
    return {
      d: `M ${p1.x} ${p1.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`,
      lx: (p1.x + p2.x) / 2,
      ly: (p1.y + p2.y) / 2,
    };
  }
  if (layoutKind === 'hub') {
    return edgeOrtho(a, b, slot);
  }
  return {
    d: edgeCurve(a, b),
    lx: (a.x + b.x) / 2,
    ly: (a.y + b.y) / 2,
  };
}

function nodeMatchesFilter(node, filterId) {
  const preset = FILTER_PRESETS.find((p) => p.id === filterId) || FILTER_PRESETS[0];
  if (preset.id === 'all') return true;
  if (preset.types) {
    return preset.types.includes((node.type || '').toLowerCase());
  }
  if (preset.layers) {
    return preset.layers.includes((node._layer || node.layer || '').toLowerCase());
  }
  return true;
}

/* ───────────────── 2D map ───────────────── */

function Diagram2d({
  diagram,
  nodes,
  edges,
  layoutHint,
  selectedId,
  onSelect,
  filter,
  svgRef,
  layoutMetaRef,
  tall,
  areas,
  onHoverNode,
}) {
  const wrapRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const dragRef = useRef(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  const flowMode = isFlowDiagram(layoutHint, nodes);
  const layoutKind = flowMode ? 'dataflow' : 'hub';
  const lanes = useMemo(() => pickLanes(layoutHint, nodes), [layoutHint, nodes]);
  const fallbackModules = useMemo(() => areasToModules(areas), [areas]);
  const layout = useMemo(
    () => layoutHub2d(nodes, edges, fallbackModules, flowMode ? 'dataflow' : 'landscape'),
    [flowMode, nodes, edges, fallbackModules],
  );
  const { positions, width, height, colW, laneHeader, topPad } = layout;

  useEffect(() => {
    if (layoutMetaRef) {
      layoutMetaRef.current = {
        width,
        height,
        positions,
        lanes,
        colW,
        topPad,
        laneHeader,
        edges,
        nodes,
        kind: layout.kind,
        layoutKind,
        bands: layout.bands,
        sideLabel: layout.sideLabel,
      };
    }
  }, [layoutMetaRef, width, height, positions, lanes, colW, topPad, laneHeader, edges, nodes]);

  const visible = useMemo(() => {
    const ids = new Set();
    Object.values(positions).forEach((p) => {
      if (nodeMatchesFilter(p.node, filter)) ids.add(p.node.id);
    });
    return ids;
  }, [positions, filter]);

  const related = useMemo(() => {
    if (!selectedId) return new Set();
    const s = new Set([selectedId]);
    edges.forEach((e) => {
      if (e.from === selectedId) s.add(e.to);
      if (e.to === selectedId) s.add(e.from);
    });
    return s;
  }, [selectedId, edges]);

  const fit = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const aw = el.clientWidth || 720;
    const ah = tall ? 520 : 400;
    const pad = 24;
    const k = Math.min(1.15, Math.max(0.45, Math.min((aw - pad) / width, (ah - pad) / height)));
    setView({
      x: (aw - width * k) / 2,
      y: (ah - height * k) / 2,
      k,
    });
  }, [width, height, tall]);

  useEffect(() => {
    fit();
  }, [fit, nodes.length]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY > 0 ? 0.92 : 1.08;
      setView((v) => {
        const nk = Math.min(2.4, Math.max(0.35, v.k * factor));
        const wx = (mx - v.x) / v.k;
        const wy = (my - v.y) / v.k;
        return {
          k: nk,
          x: mx - wx * nk,
          y: my - wy * nk,
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e) => {
    if (e.button !== 0 && e.button !== 1) return;
    // Don't pan when clicking a node
    if (e.target.closest?.('[data-node]')) return;
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      vx: view.x,
      vy: view.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setView((v) => ({
      ...v,
      x: dragRef.current.vx + dx,
      y: dragRef.current.vy + dy,
    }));
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  const uid = useMemo(() => Math.random().toString(36).slice(2, 9), []);

  const posterLayout = layout.kind === 'hub' || layout.kind === 'dataflow';
  const activeLayoutKind = layoutKind;

  const renderEdges = (onTop = false) => edges.map((e, idx) => {
    const a = positions[e.from];
    const b = positions[e.to];
    if (!a || !b) return null;
    const vis = visible.has(e.from) && visible.has(e.to);
    if (!vis && filter !== 'all') return null;
    const dim =
      selectedId
        && !related.has(e.from)
        && !related.has(e.to);
    const risk = edgeHasRisk(e);
    const dashed = edgeIsDashed(e);
    const hot = hoveredEdge === idx || (selectedId && (e.from === selectedId || e.to === selectedId));
    const dataColor = flowMode ? edgeDataTypeColor(e) : null;
    const stroke = risk
      ? '#fbbf24'
      : hot
        ? '#93c5fd'
        : dataColor
          || (dashed ? '#64748b' : posterLayout ? '#94a3b8' : '#475569');
    const marker = risk
      ? `url(#arr-risk-${uid})`
      : hot
        ? `url(#arr-hi-${uid})`
        : `url(#arr-${uid})`;
    const routed = edgePathBetween(a, b, activeLayoutKind, idx);
    const d = routed.d;
    const showLabel = flowMode ? !!e.label : (posterLayout ? !!e.label : (hot || hoveredEdge === idx) && e.label);
    const lx = routed.lx;
    const ly = routed.ly;
    const sw = flowMode ? Math.max(2, edgeStrokeWidth(e)) : edgeStrokeWidth(e);
    if (!onTop) return null;
    return (
      <g
        key={`edge-${e.from}-${e.to}-${idx}`}
        opacity={dim ? 0.12 : vis ? (dashed ? 0.65 : 1) : 0.2}
        onMouseEnter={() => setHoveredEdge(idx)}
        onMouseLeave={() => setHoveredEdge(null)}
        style={{ cursor: 'default' }}
      >
        <path
          d={d}
          fill="none"
          stroke={stroke}
          strokeWidth={sw + 2.5}
          strokeOpacity={0.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={d}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          strokeDasharray={dashed ? '7 5' : undefined}
          markerEnd={marker}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={d}
          fill="none"
          stroke="transparent"
          strokeWidth={14}
        />
        {showLabel ? (
          <g>
            <rect
              x={lx - Math.min(100, (e.label || '').length * 3.2) - 6}
              y={ly - 14}
              width={Math.min(200, (e.label || '').length * 6.4) + 12}
              height={18}
              rx={3}
              fill="#0b1220"
              stroke="#334155"
            />
            <text
              x={lx}
              y={ly - 1}
              textAnchor="middle"
              fill="#e2e8f0"
              fontSize={9}
              fontFamily="system-ui, sans-serif"
            >
              {(e.label || '').length > 36 ? `${e.label.slice(0, 34)}…` : e.label}
            </text>
          </g>
        ) : null}
      </g>
    );
  });

  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden rounded-b-xl bg-[#0b1220]"
      style={{ height: tall ? 560 : flowMode ? 520 : 480 }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        className="cursor-grab active:cursor-grabbing select-none"
        role="img"
        aria-label={diagram?.title || 'Landscape diagram'}
      >
        <defs>
          <marker
            id={`arr-${uid}`}
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill="#64748b" />
          </marker>
          <marker
            id={`arr-hi-${uid}`}
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill="#93c5fd" />
          </marker>
          <marker
            id={`arr-risk-${uid}`}
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill="#fbbf24" />
          </marker>
          <pattern
            id={`hatch-${uid}`}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill="#1e293b" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#64748b" strokeWidth="2" />
          </pattern>
        </defs>

        <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
          {posterLayout ? (
            <>
              {(layout.bands || []).map((band) => (
                <g key={band.id}>
                  <rect
                    x={4}
                    y={band.y}
                    width={width - 8}
                    height={Math.max(24, band.h)}
                    fill={
                      {
                        saas: '#172033',
                        onprem: '#1a2332',
                        desktop: '#121826',
                        interface: flowMode ? '#141028' : '#1a1530',
                        core: flowMode ? '#1e293b' : '#1a2332',
                        analytics: '#1a140c',
                      }[band.id] || '#121826'
                    }
                    opacity={flowMode ? 0.35 : 0.55}
                  />
                  <text
                    x={12}
                    y={band.y + 16}
                    fill="#94a3b8"
                    fontSize={9}
                    fontWeight={700}
                    fontFamily="system-ui, sans-serif"
                  >
                    {band.label.toUpperCase()}
                  </text>
                </g>
              ))}
              <rect
                x={2}
                y={2}
                width={width - 4}
                height={height - 4}
                fill="none"
                stroke="#334155"
                strokeWidth={1}
                rx={4}
              />
              {layout.sideLabel ? (
                <text
                  x={14}
                  y={height / 2}
                  fill="#94a3b8"
                  fontSize={9}
                  fontWeight={700}
                  fontFamily="system-ui, sans-serif"
                  transform={`rotate(-90 14 ${height / 2})`}
                  textAnchor="middle"
                >
                  {layout.sideLabel.toUpperCase()}
                </text>
              ) : null}
            </>
          ) : (
            lanes.map((lane, i) => {
              const theme = laneTheme(lane.id);
              const hasNodes = Object.values(positions).some((p) => p.layer === lane.id);
              return (
                <g key={lane.id} opacity={hasNodes ? 1 : 0.35}>
                  <rect
                    x={i * colW + 4}
                    y={topPad}
                    width={colW - 8}
                    height={height - topPad - 8}
                    rx={12}
                    fill={theme.fill}
                    stroke={theme.stroke}
                    strokeOpacity={0.45}
                    strokeWidth={1.25}
                  />
                  <rect
                    x={i * colW + 4}
                    y={topPad}
                    width={colW - 8}
                    height={22}
                    rx={12}
                    fill={theme.stroke}
                    fillOpacity={0.22}
                  />
                  <text
                    x={i * colW + colW / 2}
                    y={topPad + 15}
                    textAnchor="middle"
                    fill={theme.title}
                    fontSize={11}
                    fontWeight={700}
                    fontFamily="system-ui, sans-serif"
                  >
                    {lane.label.toUpperCase()}
                  </text>
                  <text
                    x={i * colW + colW / 2}
                    y={topPad + laneHeader - 6}
                    textAnchor="middle"
                    fill="#94a3b8"
                    fontSize={9}
                    fontFamily="system-ui, sans-serif"
                  >
                    {lane.hint}
                  </text>
                </g>
              );
            })
          )}

          {/* Node bodies (under edges) */}
          {Object.values(positions).map((p) => {
            const n = p.node;
            const isHero = !!p.hero;
            const style = posterLayout ? cardStyle(n, isHero) : colorFor(n.type);
            const isSelected = selectedId === n.id;
            const isRelated = !selectedId || related.has(n.id);
            const isVisible = visible.has(n.id);
            const dim = !isVisible || (selectedId && !isRelated);
            const x = p.x - p.w / 2;
            const y = p.y - p.h / 2;
            const fill = posterLayout && style.hatch ? `url(#hatch-${uid})` : style.fill;
            const strip = flowMode && !isHero && n._functionStrip ? functionStripStyle(n) : null;
            const stripH = strip ? 16 : 0;
            const bodyH = p.h - stripH;
            return (
              <g key={`${n.id}-bg`} opacity={dim ? 0.18 : 1}>
                <rect
                  x={x}
                  y={y}
                  width={p.w}
                  height={bodyH}
                  rx={posterLayout ? 4 : 10}
                  fill={flowMode && isHero ? '#f8fafc' : fill}
                  stroke="none"
                />
                {strip ? (
                  <rect
                    x={x}
                    y={y + bodyH}
                    width={p.w}
                    height={stripH}
                    rx={0}
                    fill={strip.fill}
                  />
                ) : null}
                <rect
                  x={x}
                  y={y}
                  width={p.w}
                  height={p.h}
                  rx={posterLayout ? 4 : 10}
                  fill="none"
                  stroke={isSelected || hoveredNode === n.id ? '#38bdf8' : style.stroke}
                  strokeWidth={isHero ? 2.2 : isSelected ? 2.5 : 1.4}
                  strokeDasharray={style.dashed ? '5 4' : undefined}
                />
              </g>
            );
          })}

          {/* Edges drawn above node fills so lines stay visible */}
          {renderEdges(true)}

          {/* Node labels & tiles (above edges) */}
          {Object.values(positions).map((p) => {
            const n = p.node;
            const isHero = !!p.hero;
            const style = posterLayout ? cardStyle(n, isHero) : colorFor(n.type);
            const isSelected = selectedId === n.id;
            const isRelated = !selectedId || related.has(n.id);
            const isVisible = visible.has(n.id);
            const dim = !isVisible || (selectedId && !isRelated);
            const mods = isHero ? (n._modules || nodeModules(n, fallbackModules)) : [];
            const featured = isHero ? n._featured : null;
            const tiles = featured ? mods.filter((m) => m.code !== featured.code) : mods;
            const lines = [
              n.subtitle,
              ...((n.metrics || []).filter(Boolean).slice(0, isHero && mods.length ? 1 : isHero ? 3 : flowMode ? 0 : 2)),
            ].filter(Boolean);
            const x = p.x - p.w / 2;
            const y = p.y - p.h / 2;
            const strip = flowMode && !isHero && n._functionStrip ? functionStripStyle(n) : null;
            const stripH = strip ? 16 : 0;
            const bodyH = p.h - stripH;
            const tileW = Math.min(72, (p.w - 20) / Math.min(4, Math.max(tiles.length, 1)) - 4);
            const tileStartY = y + 38 + (n.subtitle ? 12 : 0);
            const labelColor = flowMode && isHero ? '#0f172a' : style.text;
            const subColor = flowMode && isHero ? '#334155' : (style.sub || style.stroke);
            return (
              <g
                key={n.id}
                data-node={n.id}
                opacity={dim ? 0.18 : 1}
                style={{ cursor: 'pointer' }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onSelect(isSelected ? null : n);
                }}
                onMouseEnter={() => {
                  setHoveredNode(n.id);
                  onHoverNode?.(n);
                }}
                onMouseLeave={() => {
                  setHoveredNode(null);
                  onHoverNode?.(null);
                }}
              >
                <rect
                  x={x}
                  y={y}
                  width={p.w}
                  height={p.h}
                  fill="transparent"
                />
                <text
                  x={p.x}
                  y={y + (isHero ? 22 : flowMode ? 18 : 16)}
                  textAnchor="middle"
                  fill={labelColor}
                  fontSize={isHero ? (flowMode ? 14 : 13) : 11}
                  fontWeight={700}
                  fontFamily="system-ui, sans-serif"
                >
                  {(n.label || n.id || '').slice(0, isHero ? 40 : 22)}
                </text>
                {n.subtitle && !flowMode ? (
                  <text
                    x={p.x}
                    y={y + 32}
                    textAnchor="middle"
                    fill={subColor}
                    fontSize={9}
                    fontFamily="system-ui, sans-serif"
                  >
                    {String(n.subtitle).slice(0, 42)}
                  </text>
                ) : null}
                {flowMode && isHero && n.subtitle ? (
                  <text
                    x={p.x}
                    y={y + 38}
                    textAnchor="middle"
                    fill={subColor}
                    fontSize={10}
                    fontFamily="system-ui, sans-serif"
                  >
                    {String(n.subtitle).slice(0, 48)}
                  </text>
                ) : null}
                {strip ? (
                  <text
                    x={p.x}
                    y={y + bodyH + stripH / 2 + 4}
                    textAnchor="middle"
                    fill={strip.text}
                    fontSize={8}
                    fontWeight={700}
                    fontFamily="system-ui, sans-serif"
                  >
                    {String(n._functionStrip).slice(0, 24)}
                  </text>
                ) : null}
                {tiles.slice(0, 8).map((mod, mi) => {
                  const col = mi % 4;
                  const row = Math.floor(mi / 4);
                  const rowCount = Math.min(4, tiles.length - row * 4);
                  const rowWidth = rowCount * (tileW + 4) - 4;
                  const tx = p.x - rowWidth / 2 + col * (tileW + 4);
                  const ty = tileStartY + row * 28;
                  return (
                    <g key={`${n.id}-mod-${mod.code}-${mi}`}>
                      <rect
                        x={tx}
                        y={ty}
                        width={tileW}
                        height={22}
                        rx={3}
                        fill="#e2e8f0"
                        stroke="#0f172a"
                        strokeWidth={0.8}
                      />
                      <text
                        x={tx + tileW / 2}
                        y={ty + 15}
                        textAnchor="middle"
                        fill="#0f172a"
                        fontSize={9}
                        fontWeight={700}
                        fontFamily="system-ui, sans-serif"
                      >
                        {mod.code}
                      </text>
                    </g>
                  );
                })}
                {featured ? (
                  <g>
                    <rect
                      x={x + 10}
                      y={tileStartY + Math.ceil(tiles.slice(0, 8).length / 4) * 28 + (tiles.length ? 2 : 0)}
                      width={p.w - 20}
                      height={22}
                      rx={3}
                      fill="#e2e8f0"
                      stroke="#0f172a"
                      strokeWidth={0.8}
                    />
                    <text
                      x={p.x}
                      y={tileStartY + Math.ceil(tiles.slice(0, 8).length / 4) * 28 + 17}
                      textAnchor="middle"
                      fill="#0f172a"
                      fontSize={9}
                      fontWeight={700}
                      fontFamily="system-ui, sans-serif"
                    >
                      {`${featured.code}${featured.note ? ` — ${featured.note}` : featured.name ? ` — ${featured.name}` : ''}`.slice(0, 52)}
                    </text>
                  </g>
                ) : null}
                {lines.filter((line) => line !== n.subtitle).map((line, li) => (
                  <text
                    key={`${n.id}-m-${li}`}
                    x={p.x}
                    y={y + p.h - 10 - (lines.filter((l) => l !== n.subtitle).length - 1 - li) * 11}
                    textAnchor="middle"
                    fill={style.sub || style.stroke}
                    fontSize={8}
                    fontFamily="system-ui, sans-serif"
                  >
                    {String(line).slice(0, isHero ? 48 : 28)}
                  </text>
                ))}
              </g>
            );
          })}
        </g>
      </svg>

      <div className="pointer-events-none absolute bottom-2 right-2 flex gap-1">
        <button
          type="button"
          className="pointer-events-auto rounded-md border border-slate-600 bg-slate-900/90 px-2 py-1 text-[10px] text-zinc-300 hover:bg-slate-800"
          onClick={fit}
        >
          Fit
        </button>
        <button
          type="button"
          className="pointer-events-auto rounded-md border border-slate-600 bg-slate-900/90 px-2 py-1 text-[10px] text-zinc-300 hover:bg-slate-800"
          onClick={() => setView((v) => ({ ...v, k: Math.min(2.4, v.k * 1.15) }))}
        >
          +
        </button>
        <button
          type="button"
          className="pointer-events-auto rounded-md border border-slate-600 bg-slate-900/90 px-2 py-1 text-[10px] text-zinc-300 hover:bg-slate-800"
          onClick={() => setView((v) => ({ ...v, k: Math.max(0.35, v.k * 0.87) }))}
        >
          −
        </button>
      </div>
    </div>
  );
}

/* ───────────────── 3D explore (optional) ───────────────── */

function layout3d(nodes, edges, layoutHint) {
  const lanes = pickLanes(layoutHint, nodes);
  const deg = {};
  edges.forEach((e) => {
    deg[e.from] = (deg[e.from] || 0) + 1;
    deg[e.to] = (deg[e.to] || 0) + 1;
  });

  const hero = pickHero(nodes, edges) || nodes[0];
  const groups = { north: [], west: [], east: [], south: [] };
  nodes.forEach((n) => {
    if (n.id === hero.id) return;
    groups[regionFor(resolveLayer(n, lanes), n)].push(n);
  });
  const positioned = [{
    ...hero,
    fx: 0,
    fy: 0,
    fz: 0,
    x: 0,
    y: 0,
    z: 0,
    val: 18 + (deg[hero.id] || 0),
    _lane: 'application',
    _hero: true,
  }];
  const place = (list, ox, oy, oz, along) => {
    const span = 28;
    const start = -((list.length - 1) * span) / 2;
    list.forEach((n, i) => {
      const x = along === 'x' ? ox + start + i * span : ox;
      const y = along === 'y' ? oy + start + i * span : oy;
      positioned.push({
        ...n,
        fx: x,
        fy: y,
        fz: oz,
        x,
        y,
        z: oz,
        val: 10 + (deg[n.id] || 0),
        _lane: resolveLayer(n, lanes),
      });
    });
  };
  place(groups.north, 0, 55, -8, 'x');
  place(groups.south, 0, -58, 8, 'x');
  place(groups.west, -78, 0, 6, 'y');
  place(groups.east, 78, 0, -6, 'y');
  return { positioned, lanes, colGap: 95, kind: 'hub' };
}

function addLaneGuides(scene, lanes, colGap) {
  const depth = 90;
  lanes.forEach((lane, col) => {
    const theme = laneTheme(lane.id);
    const x = (col - (lanes.length - 1) / 2) * colGap;
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(colGap * 0.86, 1.6, depth),
      new THREE.MeshStandardMaterial({
        color: theme.fill,
        transparent: true,
        opacity: 0.72,
        emissive: theme.stroke,
        emissiveIntensity: 0.12,
        metalness: 0.1,
        roughness: 0.8,
      }),
    );
    floor.position.set(x, -58, 0);
    scene.add(floor);
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(colGap * 0.86, 120),
      new THREE.MeshStandardMaterial({
        color: theme.fill,
        transparent: true,
        opacity: 0.18,
        side: THREE.DoubleSide,
      }),
    );
    wall.position.set(x, 2, -depth / 2 + 4);
    scene.add(wall);
    const title = new SpriteText(String(lane.label || '').toUpperCase());
    title.color = theme.title;
    title.backgroundColor = 'rgba(8,12,22,0.55)';
    title.padding = 2;
    title.borderRadius = 3;
    title.textHeight = 4.6;
    title.position.set(x, 62, -depth / 2 + 8);
    scene.add(title);
  });
}

function makeNodeObject(node, selectedId) {
  const c = colorFor(node.type);
  const selected = node.id === selectedId;
  const group = new THREE.Group();
  const isHub = ['hub', 'interface', 'integration', 'middleware'].includes(
    (node.type || '').toLowerCase(),
  );
  const size = Math.min(16, Math.max(9, Number(node.val) || 10));
  const mesh = isHub
    ? new THREE.Mesh(
      new THREE.SphereGeometry(size * 0.52, 22, 16),
      new THREE.MeshStandardMaterial({
        color: c.fill,
        emissive: c.stroke,
        emissiveIntensity: selected ? 0.45 : 0.16,
        metalness: 0.3,
        roughness: 0.45,
      }),
    )
    : new THREE.Mesh(
      new THREE.BoxGeometry(size * 1.45, size * 0.85, size * 0.55),
      new THREE.MeshStandardMaterial({
        color: c.fill,
        emissive: c.stroke,
        emissiveIntensity: selected ? 0.4 : 0.14,
        metalness: 0.2,
        roughness: 0.5,
      }),
    );
  group.add(mesh);
  const lines = [
    node.label || node.id,
    node.subtitle,
    ...((node.metrics || []).slice(0, 2)),
  ].filter(Boolean);
  const label = new SpriteText(lines.map((l) => String(l).slice(0, 32)).join('\n'));
  label.color = c.text;
  label.backgroundColor = selected ? 'rgba(15,23,42,0.92)' : 'rgba(8,12,22,0.82)';
  label.padding = 2.4;
  label.borderRadius = 4;
  label.textHeight = 3.4;
  label.position.y = size * 0.9 + 8 + (lines.length - 1) * 1.4;
  group.add(label);
  return group;
}

function Diagram3d({ nodes, edges, layoutHint, selectedId, onSelect, tall, graphApiRef }) {
  const containerRef = useRef(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const graphData = useMemo(() => {
    const { positioned, lanes, colGap, kind } = layout3d(nodes, edges, layoutHint);
    const idSet = new Set(positioned.map((n) => n.id));
    return {
      nodes: positioned,
      lanes,
      colGap,
      kind,
      links: edges
        .filter((e) => e.from && e.to && idSet.has(e.from) && idSet.has(e.to))
        .map((e, i) => ({
          id: `${e.from}->${e.to}-${i}`,
          source: e.from,
          target: e.to,
          label: e.label || '',
          kind: e.kind || '',
          risk: edgeHasRisk(e),
          dashed: edgeIsDashed(e),
        })),
    };
  }, [nodes, edges, layoutHint]);

  useEffect(() => {
    if (!containerRef.current || !nodes.length) return undefined;
    let graph;
    const el = containerRef.current;
    el.innerHTML = '';
    const height = tall ? 520 : 400;

    try {
      graph = ForceGraph3D({
        controlType: 'orbit',
        rendererConfig: { antialias: true, alpha: true, preserveDrawingBuffer: true },
      })(el)
        .width(el.clientWidth || 720)
        .height(height)
        .backgroundColor('#0b0f1a')
        .showNavInfo(false)
        .graphData({
          nodes: JSON.parse(JSON.stringify(graphData.nodes)),
          links: JSON.parse(JSON.stringify(graphData.links)),
        })
        .nodeId('id')
        .nodeLabel((n) => [n.label || n.id, n.subtitle, ...(n.metrics || [])].filter(Boolean).join('\n'))
        .nodeThreeObject((n) => makeNodeObject(n, selectedIdRef.current))
        .nodeThreeObjectExtend(false)
        .linkColor((l) =>
          l.risk
            ? 'rgba(251,191,36,0.85)'
            : l.dashed
              ? 'rgba(148,163,184,0.28)'
              : 'rgba(148,163,184,0.55)',
        )
        .linkWidth((l) => (l.risk ? 1.8 : 1.15))
        .linkDirectionalArrowLength(4)
        .linkDirectionalArrowRelPos(1)
        .linkDirectionalParticles((l) => (l.dashed ? 0 : 1))
        .linkDirectionalParticleWidth(1.1)
        .linkDirectionalParticleSpeed(0.004)
        .onNodeClick((node) => {
          onSelect(node);
          graph.nodeThreeObject((n) => makeNodeObject(n, node.id));
          const nx = Number(node.x) || 0;
          const ny = Number(node.y) || 0;
          const nz = Number(node.z) || 0;
          graph.cameraPosition(
            { x: nx + 90, y: ny + 60, z: nz + 120 },
            { x: nx, y: ny, z: nz },
            800,
          );
        })
        .onBackgroundClick(() => {
          onSelect(null);
          graph.nodeThreeObject((n) => makeNodeObject(n, null));
        })
        .enableNodeDrag(true)
        .warmupTicks(0)
        .cooldownTicks(0);

      try {
        graph.d3Force('charge')?.strength(0);
        graph.d3Force('link')?.strength(0);
        graph.d3Force('center')?.strength(0);
      } catch {
        /* ignore */
      }

      const scene = graph.scene();
      scene.add(new THREE.AmbientLight(0xffffff, 0.75));
      const key = new THREE.DirectionalLight(0xb4d4ff, 0.9);
      key.position.set(60, 100, 50);
      scene.add(key);
      if (graphData.kind !== 'hub') {
        addLaneGuides(scene, graphData.lanes || [], graphData.colGap || 95);
      }

      graph.cameraPosition({ x: 20, y: 80, z: 260 }, { x: 0, y: 0, z: 0 }, 0);
      if (graphApiRef) graphApiRef.current = graph;

      const onResize = () => {
        if (!containerRef.current) return;
        graph.width(containerRef.current.clientWidth || 720);
        graph.height(height);
      };
      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('resize', onResize);
        try {
          if (typeof graph._destructor === 'function') graph._destructor();
        } catch {
          /* ignore */
        }
        if (graphApiRef) graphApiRef.current = null;
        el.innerHTML = '';
      };
    } catch {
      return undefined;
    }
  }, [graphData, nodes.length, tall, onSelect, graphApiRef]);

  // Refresh materials when selection changes without full rebuild
  useEffect(() => {
    const g = graphApiRef?.current;
    if (!g) return;
    g.nodeThreeObject((n) => makeNodeObject(n, selectedId));
  }, [selectedId, graphApiRef]);

  return (
    <div
      ref={containerRef}
      className="w-full cursor-grab active:cursor-grabbing"
      style={{ height: tall ? 520 : 400 }}
    />
  );
}

/* ───────────────── Shell ───────────────── */

function PosterHeader({ header, title, caption }) {
  if (!header && !title && !caption) return null;
  const chips = [
    header?.customer && header.customerNumber
      ? `${header.customer} · ${header.customerNumber}`
      : header?.customer,
    header?.sid && `SID ${header.sid}`,
    header?.target,
    header?.date,
  ].filter(Boolean);
  return (
    <div className="border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 px-3 py-2">
      {title ? (
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-200/90">
          {title}
        </p>
      ) : null}
      {chips.length ? (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="rounded-md border border-slate-600/80 bg-slate-900/80 px-2 py-0.5 text-[10px] text-zinc-200"
            >
              {chip}
            </span>
          ))}
        </div>
      ) : null}
      {caption ? (
        <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-500">{caption}</p>
      ) : null}
    </div>
  );
}

const TransformationDiagram = forwardRef(function TransformationDiagram(
  { diagram, emptyLabel, tall, header, areas, inventory },
  ref,
) {
  const svgRef = useRef(null);
  const layoutMetaRef = useRef(null);
  const graphApiRef = useRef(null);
  const [mode, setMode] = useState('2d'); // '2d' | '3d'
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  const nodes = useMemo(
    () => (Array.isArray(diagram?.nodes) ? diagram.nodes : []),
    [diagram],
  );
  const edges = useMemo(
    () => (Array.isArray(diagram?.edges) ? diagram.edges : []),
    [diagram],
  );
  const layoutHint = (diagram?.layout || 'flow').toLowerCase();
  const flowLegend = isFlowDiagram(layoutHint, nodes);

  const onSelect = useCallback((node) => {
    setSelected(node);
  }, []);

  const onHoverNode = useCallback((node) => {
    setHovered(node);
  }, []);

  const nodeInsight = useCallback((node) => {
    if (!node) return null;
    const glossary = lookupExternalSystemForNode(node, { inventory });
    return {
      source: diagramNodeSource(node),
      glossary,
    };
  }, [inventory]);

  /** Clean full-diagram SVG for report (identity transform, fixed viewBox). */
  const exportSvgHtml = useCallback(() => {
    const meta = layoutMetaRef.current;
    if (!meta || !meta.positions) {
      // fallback to viewport SVG
      const svg = svgRef.current;
      if (!svg) return '';
      try {
        const clone = svg.cloneNode(true);
        let str = new XMLSerializer().serializeToString(clone);
        if (!str.includes('xmlns=')) {
          str = str.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        return str;
      } catch {
        return '';
      }
    }

    const { width, height, positions, lanes, colW, topPad, kind } = meta;
    const esc = (s) =>
      String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    let body = '';
    if (kind === 'hub' || kind === 'dataflow') {
      (meta.bands || []).forEach((band) => {
        body += `<text x="12" y="${band.y + 14}" fill="#94a3b8" font-size="9" font-weight="700" font-family="system-ui,sans-serif">${esc((band.label || '').toUpperCase())}</text>`;
      });
    } else {
      (lanes || []).forEach((lane, i) => {
        const theme = laneTheme(lane.id);
        body += `<rect x="${i * colW + 4}" y="${topPad}" width="${colW - 8}" height="${height - topPad - 8}" rx="12" fill="${theme.fill}" stroke="${theme.stroke}"/>`;
        body += `<text x="${i * colW + colW / 2}" y="${topPad + 16}" text-anchor="middle" fill="${theme.title}" font-size="11" font-weight="700" font-family="system-ui,sans-serif">${esc(lane.label.toUpperCase())}</text>`;
      });
    }

    edges.forEach((e, idx) => {
      const a = positions[e.from];
      const b = positions[e.to];
      if (!a || !b) return;
      const exportKind = kind === 'dataflow' ? 'dataflow' : (kind === 'hub' ? 'hub' : 'flow');
      const routed = edgePathBetween(a, b, exportKind, idx);
      const d = routed.d;
      const dataColor = kind === 'dataflow' ? edgeDataTypeColor(e) : null;
      const stroke = edgeHasRisk(e) ? '#fbbf24' : (dataColor || '#94a3b8');
      const dash = edgeIsDashed(e) ? ' stroke-dasharray="7 5"' : '';
      body += `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${edgeStrokeWidth(e)}"${dash}/>`;
      if (e.label) {
        body += `<text x="${routed.lx}" y="${routed.ly - 4}" text-anchor="middle" fill="#cbd5e1" font-size="9" font-family="system-ui,sans-serif">${esc(e.label.slice(0, 40))}</text>`;
      }
    });

    Object.values(positions).forEach((p) => {
      const n = p.node;
      const poster = kind === 'hub' || kind === 'dataflow';
      const style = poster ? cardStyle(n, !!p.hero) : colorFor(n.type);
      const x = p.x - p.w / 2;
      const y = p.y - p.h / 2;
      const metric = (n.metrics && n.metrics[0]) || n.subtitle || '';
      const dash = style.dashed ? ' stroke-dasharray="5 4"' : '';
      body += `<rect x="${x}" y="${y}" width="${p.w}" height="${p.h}" rx="${poster ? 4 : 10}" fill="${kind === 'dataflow' && p.hero ? '#f8fafc' : style.fill}" stroke="${style.stroke}" stroke-width="${p.hero ? 2.2 : 1.5}"${dash}/>`;
      body += `<text x="${p.x}" y="${metric ? p.y - 6 : p.y + 4}" text-anchor="middle" fill="${style.text}" font-size="12" font-weight="600" font-family="system-ui,sans-serif">${esc((n.label || n.id || '').slice(0, 28))}</text>`;
      if (metric) {
        body += `<text x="${p.x}" y="${p.y + 12}" text-anchor="middle" fill="${style.sub || style.stroke}" font-size="9" font-family="system-ui,sans-serif">${esc(String(metric).slice(0, 32))}</text>`;
      }
    });

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${Math.min(width, 900)}" height="${Math.min(height, 520)}" style="background:#0b1220;border-radius:8px;">${body}</svg>`;
  }, [edges]);

  const exportPngFrom3d = useCallback(() => {
    const g = graphApiRef.current;
    if (!g) return '';
    try {
      const renderer = typeof g.renderer === 'function' ? g.renderer() : g.renderer;
      const camera = typeof g.camera === 'function' ? g.camera() : g.camera;
      const scene = typeof g.scene === 'function' ? g.scene() : g.scene;
      if (renderer && scene && camera) renderer.render(scene, camera);
      return renderer?.domElement?.toDataURL?.('image/png') || '';
    } catch {
      return '';
    }
  }, []);

  const exportHtml = useCallback(() => {
    // Prefer clean 2D SVG for reports (readable). 3D PNG only if user is in explore and 2D meta missing.
    const svg = exportSvgHtml();
    if (svg) return svg;
    const dataUrl = exportPngFrom3d();
    if (!dataUrl) return '';
    return `<img alt="${(diagram?.title || 'diagram').replace(/"/g, '')}" src="${dataUrl}" style="max-width:100%;height:auto;background:#0b0f1a;border-radius:8px;" />`;
  }, [exportSvgHtml, exportPngFrom3d, diagram?.title]);

  useImperativeHandle(ref, () => ({ exportHtml, exportPng: exportPngFrom3d }), [
    exportHtml,
    exportPngFrom3d,
  ]);

  if (!nodes.length) {
    return (
      <p className="text-sm text-muted-foreground">{emptyLabel || 'No diagram data yet.'}</p>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-xl border border-[var(--line)] bg-[#0b0f1a]"
      data-testid="transformation-diagram"
    >
      <PosterHeader header={header} title={diagram?.title} caption={diagram?.caption} />
      {/* Toolbar */}
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-800 px-3 py-2">
        <div className="min-w-0">
          <p className="text-[11px] text-zinc-500">
            {mode === '2d'
              ? 'Architecture map · drag to pan · scroll to zoom · hover for source · click a node'
              : 'Same map in 3D · drag to orbit · scroll zoom · click a node to focus'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="inline-flex rounded-md border border-slate-600 p-0.5">
            <button
              type="button"
              className={`rounded px-2 py-1 text-[11px] ${
                mode === '2d'
                  ? 'bg-slate-700 text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
              onClick={() => setMode('2d')}
            >
              Map
            </button>
            <button
              type="button"
              className={`rounded px-2 py-1 text-[11px] ${
                mode === '3d'
                  ? 'bg-slate-700 text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
              onClick={() => setMode('3d')}
            >
              Explore 3D
            </button>
          </div>
          {FILTER_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`rounded-md border px-2 py-1 text-[10px] ${
                filter === p.id
                  ? 'border-sky-500/50 bg-sky-500/15 text-sky-200'
                  : 'border-slate-600 bg-slate-900/60 text-zinc-400 hover:text-zinc-200'
              }`}
              onClick={() => setFilter(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-800/80 px-3 py-1.5">
        {flowLegend ? (
          <>
            <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-500 px-1.5 py-0.5 text-[10px] text-white">Sources</span>
            <span className="inline-flex items-center gap-1 rounded border border-violet-300 bg-violet-900 px-1.5 py-0.5 text-[10px] text-violet-100">Interface channels</span>
            <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-900">Core ERP</span>
            <span className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-900">Destinations</span>
            <span className="inline-flex items-center gap-1 rounded border border-orange-400 bg-orange-950 px-1.5 py-0.5 text-[10px] text-orange-100">Analytics</span>
            {DATA_TYPE_COLORS.slice(0, 6).map((dt) => (
              <span
                key={dt.label}
                className="inline-flex items-center gap-1 rounded border border-slate-600 px-1.5 py-0.5 text-[10px] text-zinc-200"
              >
                <span className="inline-block h-2 w-4 rounded-sm" style={{ background: dt.color }} />
                {dt.label}
              </span>
            ))}
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-900">On-premise</span>
            <span className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-500 px-1.5 py-0.5 text-[10px] text-white">SaaS / cloud</span>
            <span className="inline-flex items-center gap-1 rounded border border-slate-400 bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-100">Desktop / user</span>
            <span className="inline-flex items-center gap-1 rounded border border-dashed border-slate-400 bg-slate-950 px-1.5 py-0.5 text-[10px] text-slate-300">Configured, unused</span>
          </>
        )}
        <span className="ml-1 self-center text-[10px] text-zinc-500">
          {flowLegend
            ? 'Hub-and-spoke dataflow · colored arrows by data type · edges render above blocks'
            : 'Thicker edges = higher volumes · amber = unavailable/blocked · dashed = unused'}
        </span>
      </div>

      <div className="border-b border-slate-800/80 px-3 py-2">
        <AuraExternalSystemsLegend
          inventory={inventory}
          nodes={nodes}
          compact
          diagramOnly
          title="Systems in this diagram"
          description="Each entry is identified and described by the Analysis Agent for this readiness package."
        />
      </div>

      <div className="relative">
        {/* Keep 2D mounted (hidden) when exploring 3D so layout meta + PDF export stay available */}
        <div className={mode === '2d' ? 'block' : 'hidden'}>
          <Diagram2d
            diagram={diagram}
            nodes={nodes}
            edges={edges}
            layoutHint={layoutHint}
            selectedId={selected?.id || null}
            onSelect={onSelect}
            filter={filter}
            svgRef={svgRef}
            layoutMetaRef={layoutMetaRef}
            tall={tall}
            areas={areas}
            onHoverNode={onHoverNode}
          />
        </div>
        {mode === '3d' && (
          <Diagram3d
            nodes={nodes}
            edges={edges}
            layoutHint={layoutHint}
            selectedId={selected?.id || null}
            onSelect={onSelect}
            tall={tall}
            graphApiRef={graphApiRef}
          />
        )}

        {hovered && (!selected || hovered.id !== selected.id) && (() => {
          const insight = nodeInsight(hovered);
          return (
            <div className="absolute right-3 top-3 z-10 max-w-xs rounded-lg border border-sky-500/30 bg-slate-900/95 p-3 text-xs shadow-xl backdrop-blur">
              <div className="text-[10px] uppercase tracking-wide text-sky-400/90">Hover · source</div>
              <div className="mt-0.5 text-sm font-semibold text-zinc-100">
                {hovered.label || hovered.id}
              </div>
              {insight?.glossary ? (
                <p className="mt-1 text-zinc-300">{insight.glossary.description}</p>
              ) : null}
              {insight?.glossary?.identificationSource ? (
                <p className="mt-1 text-[10px] text-zinc-500">
                  {insight.glossary.identificationSource}
                </p>
              ) : null}
              <p className="mt-2 border-t border-slate-700 pt-2 text-[11px] leading-relaxed text-zinc-400">
                {insight?.source}
              </p>
            </div>
          );
        })()}

        {selected && (
          <div className="absolute bottom-3 left-3 z-10 max-w-sm rounded-lg border border-slate-600/80 bg-slate-900/95 p-3 text-xs shadow-xl backdrop-blur">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Selected</div>
            <div className="mt-0.5 text-sm font-semibold text-zinc-100">
              {selected.label || selected.id}
            </div>
            {selected.subtitle && (
              <div className="text-zinc-400">{selected.subtitle}</div>
            )}
            {(() => {
              const insight = nodeInsight(selected);
              if (!insight?.glossary) return null;
              return (
                <>
                  <p className="mt-1.5 text-zinc-300">{insight.glossary.description}</p>
                  {insight.glossary.identificationSource ? (
                    <p className="mt-1 text-[10px] text-zinc-500">
                      {insight.glossary.identificationSource}
                    </p>
                  ) : null}
                </>
              );
            })()}
            <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
              {nodeInsight(selected)?.source}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {selected.type && (
                <span className="rounded bg-slate-800 px-1.5 py-0.5 text-zinc-300">
                  {selected.type}
                </span>
              )}
              {selected.layer && (
                <span className="rounded bg-slate-800 px-1.5 py-0.5 text-zinc-300">
                  {selected.layer}
                </span>
              )}
            </div>
            {Array.isArray(selected.metrics) && selected.metrics.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-4 text-zinc-300">
                {selected.metrics.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            {(() => {
              const connected = edges.filter(
                (e) => e.from === selected.id || e.to === selected.id,
              );
              if (!connected.length) return null;
              return (
                <div className="mt-2 border-t border-slate-700 pt-2">
                  <div className="mb-1 text-[10px] uppercase text-zinc-500">Connections</div>
                  <ul className="max-h-28 space-y-1 overflow-y-auto text-zinc-300">
                    {connected.slice(0, 8).map((e, i) => (
                      <li key={i}>
                        <span className="text-zinc-500">
                          {e.from === selected.id ? '→' : '←'}{' '}
                        </span>
                        {e.from === selected.id ? e.to : e.from}
                        {e.label ? (
                          <span className="text-zinc-500"> · {e.label}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <button
              type="button"
              className="mt-2 text-[11px] text-sky-400 hover:text-sky-300"
              onClick={() => setSelected(null)}
            >
              Clear selection
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default TransformationDiagram;
